import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import React, { Fragment } from 'react';

import DashboardStatusDot from '@sophocles/components/BasicDashboardCard/DashboardStatusDot';

/**
 * Rail variant of {@link BasicDashboardCard}: a flat sidebar section with no
 * card chrome — a neutral overline header and status shown as an inline dot.
 *
 * Presentational only; the menu open/anchor state is owned by BasicDashboardCard
 * (which renders either this rail variant or the card variant, never both).
 */
const HEADER_MIN_HEIGHT = 32;
const MENU_ICON_SIZE = 18;
const MAX_HEADER_LINES = 4;

const lineClampSx = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: MAX_HEADER_LINES,
  wordBreak: 'break-word',
};

export default function BasicDashboardCardRail(props) {
  const {
    title,
    subtitle,
    dotColor,
    toolbarExtras,
    menuItems,
    actions,
    children,
    anchorEl,
    open,
    onMenuClick,
    onMenuClose,
  } = props;

  const hasHeader = Boolean(title || subtitle || dotColor || toolbarExtras || menuItems);

  return (
    <Box sx={{ width: '100%' }}>
      {hasHeader ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            // 12px top/side padding matches the relay card header, so header
            // actions (settings cog, chips) land at the same offset on every
            // dashboard card.
            px: 'var(--space-3)',
            pt: 'var(--space-3)',
            pb: 'var(--space-1)',
            minHeight: HEADER_MIN_HEIGHT,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', minWidth: 0 }}>
            <DashboardStatusDot color={dotColor} sx={{ mt: 0.75 }} />
            {title || subtitle ? (
              <Box sx={{ minWidth: 0 }}>
                {title ? (
                  <Typography
                    variant="body1"
                    sx={{
                      ...lineClampSx,
                      color: 'text.primary',
                      fontWeight: 700,
                      lineHeight: 1.25,
                      fontSize: '0.95rem',
                    }}
                  >
                    {title}
                  </Typography>
                ) : null}
                {subtitle ? (
                  <Typography
                    variant="body2"
                    sx={{
                      ...lineClampSx,
                      color: 'text.secondary',
                      fontWeight: 400,
                      lineHeight: 1.35,
                      mt: title ? 0.25 : 0,
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {toolbarExtras}
            {menuItems ? (
              <Fragment>
                <IconButton
                  size="small"
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                  onClick={onMenuClick}
                >
                  <MoreVertIcon sx={{ color: 'text.secondary', fontSize: MENU_ICON_SIZE }} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClick={onMenuClose} onClose={onMenuClose}>
                  {menuItems}
                </Menu>
              </Fragment>
            ) : null}
          </Box>
        </Box>
      ) : null}
      {children}
      {actions ? <Box sx={{ px: 'var(--space-3)', pb: 'var(--space-1)' }}>{actions}</Box> : null}
    </Box>
  );
}
